import { Box } from '@mui/material';
import { ComponentProps, useEffect, useMemo, useRef } from 'react';
import * as React from 'react';
import { createPortal } from 'react-dom';
import {
  Control,
  FieldValues,
  useFieldArray,
  UseFieldArrayReturn,
} from 'react-hook-form';

import { AdditionalItemRendererProps } from '@/components/form/formAwareInputs/FormAwareInput.types';
import { DuplicatedInputFieldGroup } from '@/components/form/formAwareInputs/FormAwareInputRepeater/DuplicatedInputFieldGroup';
import { InputFieldItem } from '@/components/form/formAwareInputs/FormAwareInputRepeater/InputFieldItem';
import {
  DuplicatedFieldGroupWithoutArrayFieldFullPath,
  RepeaterInputShape,
} from '@/components/form/formAwareInputs/formAwareRepeater/FormAwareRepeater.types';
import {
  DELETE_ROW_BUTTON_WIDTH_PX,
  groupDuplicateFields,
} from '@/components/form/formAwareInputs/formAwareRepeater/FormAwareRepeater.utils';
import { PlusIcon } from '@/components/icons/PlusIcon';
import { FormLayoutItem, FormLayoutRow } from '@/components/layout/FormLayout';
import { useFormContext } from '@/components/react-hook-form';
import { ArrayFieldNameFromFormShape } from '@/types/react-hook-form';

import { Button } from '../../baseInputs/Button';
import { useFormFieldsDisabled } from '../../context/formFieldsDisabled.context';

export interface FormAwareInputRepeaterProps<
  FormShape extends FieldValues,
  FieldArrayKeyName extends string = 'id',
> {
  name: ArrayFieldNameFromFormShape<FormShape>;
  allowEmpty?: boolean;
  // When true, the delete button is shown even for the sole row, but deleting
  // it replaces the row with `emptyValue` instead of leaving an empty array —
  // i.e. there is always at least one (possibly empty) row visible.
  keepAtLeastOneRow?: boolean;
  control: Control<FormShape>;
  render: (
    itemIndex: number,
    fieldArrayProps: Pick<
      UseFieldArrayReturn<
        FormShape,
        ArrayFieldNameFromFormShape<FormShape>,
        FieldArrayKeyName
      >,
      'swap' | 'fields' | 'remove'
    >,
    // This id is the auto-generated id from the useFieldArray hook, to keep
    // track of the fields in the array.
    id: string,
    additionalItemRendererProps?: AdditionalItemRendererProps
  ) => React.ReactNode;
  emptyValue: RepeaterInputShape<FormShape>;
  addAnotherItemText: string;
  addAnotherItemButtonProps?: ComponentProps<typeof Button>;
  // rightFooterContent, if passed, sits to the right of the "add" button
  rightFooterContent?: React.ReactNode;
  shouldInit?: boolean;
  maxItems?: number;

  // Set this to true when FormAwareInputRepeater is used to render
  // a list of single line inputs
  isSingleLineRepeatedInput?: boolean;

  // This is the key name for useFieldArray to use to store the auto-generated
  // id for each item in the array.
  fieldArrayKeyName?: FieldArrayKeyName;

  // This is the name of the field in the input shape that is used to identify
  // the input. This is used to identify and group duplicates.
  idField?: keyof RepeaterInputShape<FormShape>;
  shouldGroupDuplicates?: boolean;
  actionRowSx?: ComponentProps<typeof FormLayoutRow>['sx'];
  duplicatesRef?: React.RefObject<HTMLDivElement | null>;
}

export function FormAwareInputRepeater<
  FormShape extends FieldValues,
  FieldArrayKeyName extends string = 'id',
>({
  emptyValue,
  shouldInit,
  allowEmpty,
  keepAtLeastOneRow = false,
  name,
  control,
  addAnotherItemText,
  addAnotherItemButtonProps,
  rightFooterContent,
  render,
  isSingleLineRepeatedInput = true,
  maxItems = Infinity,
  fieldArrayKeyName = 'id' as FieldArrayKeyName,
  idField = 'id',
  shouldGroupDuplicates = false,
  actionRowSx = {},
  duplicatesRef,
}: FormAwareInputRepeaterProps<FormShape, FieldArrayKeyName>) {
  const { disabled: disabledFromContext } = useFormFieldsDisabled();
  const needsInitialization = useRef(shouldInit);
  const { reset } = useFormContext();
  const { fields, append, remove, swap, replace } = useFieldArray({
    control,
    name,
    keyName: fieldArrayKeyName,
    rules: {
      maxLength: maxItems,
    },
  });

  // When `keepAtLeastOneRow` is set, deleting the last remaining row resets it
  // to an empty row rather than emptying the array, so a row is always shown.
  const removeItem: typeof remove = (index) => {
    if (keepAtLeastOneRow && fields.length <= 1) {
      // `replace` types its arg more strictly than `append` (which already
      // accepts `emptyValue`), so cast to the same field-array element type.
      replace([emptyValue] as Parameters<typeof replace>[0]);
      return;
    }
    remove(index);
  };

  const { sx: addAnotherItemButtonSx, ...restAddAnotherItemButtonProps } =
    addAnotherItemButtonProps || {};

  useEffect(() => {
    if (fields.length === 0 && needsInitialization.current) {
      append(emptyValue, { shouldFocus: false });
      // This is a hack to prevent appending on form initialization
      // from setting the form state to dirty
      reset({}, { keepValues: true });

      needsInitialization.current = false;
    }
  }, [append, fields.length, needsInitialization, emptyValue, name, reset]);

  const showDeleteButton = allowEmpty || keepAtLeastOneRow || fields.length > 1;

  const groupedFields: DuplicatedFieldGroupWithoutArrayFieldFullPath<
    FormShape,
    FieldArrayKeyName
  >[] = useMemo(() => {
    const groupedFields = groupDuplicateFields<FormShape, FieldArrayKeyName>(
      fields,
      idField,
      fieldArrayKeyName,
      shouldGroupDuplicates
    );

    return groupedFields;
  }, [fieldArrayKeyName, fields, idField, shouldGroupDuplicates]);

  return (
    <Box>
      <Box mb={1}>
        {groupedFields.map((group) => {
          const { id, suggestedFieldWithIndex, existingFieldWithIndex } = group;

          const commonProps = {
            render,
            fieldArrayProps: { swap, fields, remove: removeItem },
            showDeleteButton,
            isSingleLineRepeatedInput,
          };

          if (suggestedFieldWithIndex && existingFieldWithIndex) {
            return duplicatesRef?.current
              ? createPortal(
                  <DuplicatedInputFieldGroup<FormShape, FieldArrayKeyName>
                    key={id} // This is the idField value
                    fieldArrayKeyName={fieldArrayKeyName}
                    group={{
                      id,
                      suggestedFieldWithIndex,
                      existingFieldWithIndex,
                      arrayFieldFullPath: name,
                    }}
                    {...commonProps}
                  />,
                  duplicatesRef?.current
                )
              : null;
          }

          const fieldWithIndex =
            suggestedFieldWithIndex || existingFieldWithIndex;

          if (!fieldWithIndex) return null;

          return (
            <InputFieldItem<FormShape, FieldArrayKeyName>
              key={fieldWithIndex.field[fieldArrayKeyName]}
              i={fieldWithIndex.index}
              value={fieldWithIndex.field}
              fieldArrayKeyName={fieldArrayKeyName}
              {...commonProps}
            />
          );
        })}
      </Box>
      <FormLayoutRow
        sx={{
          // without this, when there's more than one item and the delete
          // buttons show up, any right footer content won't be aligned with the
          // contents of the rows.
          marginRight: showDeleteButton ? `${DELETE_ROW_BUTTON_WIDTH_PX}px` : 0,
          ...actionRowSx,
        }}
        columns={3}
      >
        <FormLayoutItem width={rightFooterContent ? 2 : 3}>
          {fields.length < maxItems && (
            <Button
              size="sm"
              sx={{ mt: 0, py: 0.5, ...addAnotherItemButtonSx }}
              variant="transparent"
              startIcon={PlusIcon}
              onClick={() => append(emptyValue, { shouldFocus: true })}
              disabled={disabledFromContext}
              {...restAddAnotherItemButtonProps}
            >
              {addAnotherItemText}
            </Button>
          )}
        </FormLayoutItem>
        {rightFooterContent && (
          <FormLayoutItem width={1}>{rightFooterContent}</FormLayoutItem>
        )}
      </FormLayoutRow>
    </Box>
  );
}
